import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Printer, Download } from "lucide-react";

function fmt(iso, dateOnly = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  return dateOnly ? d.toLocaleDateString() : d.toLocaleString();
}

const CB = ({ on }) => <span className="inline-block w-3.5 h-3.5 border-2 border-gray-900 mr-1.5 align-middle text-center leading-[10px] text-xs">{on ? "✓" : ""}</span>;

const NEIGH_LABELS = { residential: "Residential", commercial: "Commercial", other: "Other" };
const SOIL_LABELS = { virgin_material: "Virgin Material", urban_fill: "Urban Fill", other: "Other" };
const ODOR_LABELS = { none: "None", slight: "Slight odor", strong: "Strong odor" };

export default function BOLView() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  const loadAll = useCallback(async () => {
    const l = await api.get(`/loads/${id}`);
    const load = l.data;
    const [sites, trucks, drivers, projects, haulers, stockpiles] = await Promise.all([
      api.get("/sites"), api.get("/trucks"), api.get("/drivers"), api.get("/projects"), api.get("/haulers"), api.get("/stockpiles"),
    ]);
    const pickup = sites.data.find((s) => s.id === load.pickup_site_id) || {};
    const dropoff = sites.data.find((s) => s.id === load.dropoff_site_id) || {};
    const truck = trucks.data.find((t) => t.id === load.truck_id) || {};
    const driver = drivers.data.find((d) => d.id === load.driver_id) || {};
    const project = projects.data.find((p) => p.id === load.project_id) || null;
    const hauler = truck.hauler_id ? haulers.data.find((h) => h.id === truck.hauler_id) : null;
    const stockpile = load.status === "delivered"
      ? stockpiles.data.find((sp) => (sp.movements || []).some((m) => m.load_id === load.id))
      : null;
    setData({ load, pickup, dropoff, truck, driver, project, hauler, stockpile });
  }, [id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (!data) return <div className="p-8 text-gray-500">Loading BOL…</div>;
  const { load, pickup, dropoff, truck, driver, project, hauler, stockpile } = data;
  const vc = new Set(load.visible_contamination || []);

  return (
    <div className="min-h-screen bg-gray-50 p-6 print:p-0 print:bg-white">
      <div className="max-w-5xl mx-auto mb-4 flex items-center justify-between no-print">
        <Link to="/loads" className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2" data-testid="bol-back">
          <ArrowLeft className="w-4 h-4" /> Back to Loads
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={() => window.print()} data-testid="bol-print" className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 text-xs uppercase tracking-widest font-semibold">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/loads/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="bol-pdf" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-xs uppercase tracking-widest font-semibold">
            <Download className="w-3.5 h-3.5" /> Download PDF
          </a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto bg-white border-2 border-gray-900 print-container" data-testid="bol-document">
        {/* Header row */}
        <div className="grid grid-cols-12 border-b-2 border-gray-900">
          <div className="col-span-4 p-4 border-r-2 border-gray-900">
            <div className="label-caps mb-1">Project #/Name</div>
            <div className="font-mono text-sm text-gray-900">{project ? `${project.code} · ${project.name}` : "—"}</div>
          </div>
          <div className="col-span-5 p-4 border-r-2 border-gray-900 text-center flex flex-col justify-center">
            <div className="font-display font-black text-4xl tracking-tight">Bill of Lading</div>
            <div className="text-[10px] tracking-widest uppercase text-gray-600 mt-1 font-mono">MDC · v{load.manifest_version || 1}</div>
          </div>
          <div className="col-span-3 p-4">
            <div className="label-caps mb-1 text-right">No.</div>
            <div className="font-mono font-bold text-lg text-right" data-testid="bol-number">{load.bol_number}</div>
          </div>
        </div>

        {/* Dates row */}
        <div className="grid grid-cols-12 border-b-2 border-gray-900">
          <div className="col-span-4 p-3 border-r-2 border-gray-900">
            <div className="label-caps">Date Load Originated</div>
            <div className="text-sm text-gray-900 mt-0.5 font-mono">{fmt(load.created_at, true)}</div>
          </div>
          <div className="col-span-5 p-3 border-r-2 border-gray-900">
            <div className="label-caps">Street Address (Pickup)</div>
            <div className="text-sm text-gray-900 mt-0.5">{pickup.address || "—"}</div>
          </div>
          <div className="col-span-3 p-3">
            <div className="label-caps">Date Load Delivered</div>
            <div className="text-sm text-gray-900 mt-0.5 font-mono">{fmt(load.delivered_at, true)}</div>
          </div>
        </div>

        {/* MDC Inspector - Field Observation */}
        <div className="border-b-2 border-gray-900">
          <div className="bg-gray-100 border-b-2 border-gray-900 py-1.5 text-center font-display font-bold text-sm tracking-tight">MDC INSPECTOR — FIELD OBSERVATION</div>
          {[
            { label: "Neighborhood", value: load.neighborhood, opts: NEIGH_LABELS, other: load.neighborhood_other },
            { label: "Soil Type", value: load.soil_class, opts: SOIL_LABELS, other: load.soil_class_other },
            { label: "Petroleum Odor", value: load.petroleum_odor, opts: ODOR_LABELS },
            { label: "Visible contamination *", multi: true },
          ].map((row, i) => (
            <div key={i} className="grid grid-cols-12 border-b border-gray-200 last:border-b-0 text-sm">
              <div className="col-span-3 px-3 py-2 bg-gray-50 label-caps flex items-center">{row.label}</div>
              {row.multi ? (
                <>
                  <div className="col-span-3 px-3 py-2 flex items-center"><CB on={vc.has("unknown_substance")} /> Unknown Substance</div>
                  <div className="col-span-3 px-3 py-2 flex items-center"><CB on={vc.has("staining")} /> Staining</div>
                  <div className="col-span-3 px-3 py-2 flex items-center"><CB on={vc.has("free_phase_oil")} /> Free Phase Oil</div>
                </>
              ) : (
                Object.entries(row.opts).map(([k, label]) => (
                  <div key={k} className={`${k === "other" ? "col-span-3" : "col-span-3"} px-3 py-2 flex items-center gap-1`}>
                    <CB on={row.value === k} /> {label}
                    {k === "other" && row.value === "other" && row.other && <span className="ml-1 font-mono text-xs text-gray-700">: {row.other}</span>}
                  </div>
                ))
              )}
            </div>
          ))}
          <div className="px-3 py-1.5 text-[10px] italic text-red-700 border-t border-gray-200">
            * — Notify Fuss &amp; O'Neill if Free Phase oil or other obvious contamination
          </div>
        </div>

        {/* Generator / Transporter */}
        <div className="grid grid-cols-2 border-b-2 border-gray-900">
          <div className="border-r-2 border-gray-900">
            <div className="bg-gray-100 border-b-2 border-gray-900 py-1.5 text-center font-display font-bold text-sm">GENERATOR INFORMATION</div>
            <div className="p-4 space-y-2 text-sm">
              <FR label="Generator name" value={project?.name || pickup.name || "—"} />
              <FR label="Address" value={pickup.address} />
              <FR label="Project Name" value={project?.name || "—"} />
              <FR label="Material Description" value={load.material_description || load.soil_type} />
              <FR label="Container" value={load.container_type || "Dump Truck / Trailer"} />
              <FR label="Quantity" value={`${load.weight_tons} tons · ${load.volume_yards} yd³`} mono />
              <div className="pt-3 mt-3 border-t border-gray-200 space-y-2">
                <FR label="Name & Title" value={load.generator_signer || "____________________"} />
                <FR label="Date" value={fmt(load.created_at, true)} mono />
                <div>
                  <div className="label-caps">Signature</div>
                  <div className="border-b border-gray-900 h-8 mt-1"></div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mt-1">MDC Inspector</div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-gray-100 border-b-2 border-gray-900 py-1.5 text-center font-display font-bold text-sm">TRANSPORTER INFORMATION</div>
            <div className="p-4 space-y-2 text-sm">
              <FR label="Name of Company" value={hauler?.company_name || "—"} />
              <FR label="Truck Number" value={`${truck.truck_number || "—"} · Plate: ${truck.plate || "—"}`} mono />
              <FR label="DOT #" value={truck.dot_number || "—"} mono />
              <FR label="Driver" value={`${driver.name || "—"} · CDL: ${driver.cdl_number || "—"}`} />
              <div className="pt-8 mt-3 border-t border-gray-200 space-y-3">
                <div>
                  <div className="label-caps">Driver Signature</div>
                  <div className="border-b border-gray-900 h-8 mt-1"></div>
                </div>
                <div>
                  <div className="label-caps">Signature</div>
                  <div className="border-b border-gray-900 h-8 mt-1"></div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mt-1">Company Representative</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* For Facility Use Only */}
        <div>
          <div className="bg-gray-200 border-b-2 border-gray-900 px-4 py-1.5 flex justify-between items-center">
            <div className="font-display font-bold text-sm">FOR FACILITY</div>
            <div className="font-display font-bold text-sm">USE ONLY</div>
          </div>
          <div className="p-3 border-b border-gray-900 text-xs italic text-gray-800">
            If field observation indicates visible contamination, petroleum odor, staining or free phase liquid — facility will stockpile the soil in a bin designated for contaminated soil.
          </div>
          <div className="grid grid-cols-4 divide-x divide-gray-200 text-sm">
            <div className="p-3">
              <div className="label-caps">Bin #</div>
              <div className="font-mono text-gray-900 mt-1">{stockpile?.bin_number || "____________"}</div>
            </div>
            <div className="p-3">
              <div className="label-caps">Weight (tons)</div>
              <div className="font-mono text-gray-900 mt-1">{load.weight_tons}</div>
            </div>
            <div className="p-3">
              <div className="label-caps">PO Number</div>
              <div className="font-mono text-gray-900 mt-1">{load.facility_po_number || "____________"}</div>
            </div>
            <div className="p-3">
              <div className="label-caps">Signature</div>
              <div className="border-b border-gray-900 h-6 mt-1"></div>
            </div>
          </div>
        </div>

        {/* Custody trail — kept for auditor visibility */}
        {load.custody_events?.length > 0 && (
          <div className="border-t-2 border-gray-900 p-3" data-testid="bol-custody-trail">
            <div className="label-caps mb-1">Chain of Custody Trail</div>
            <div className="space-y-0.5 text-[10px] font-mono">
              {load.custody_events.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-gray-700">
                  <span className="text-gray-500">{fmt(e.at)}</span>
                  <span className="uppercase tracking-widest font-bold text-gray-900">{e.event_type.replace("_", " ")}</span>
                  {e.notes && <span className="text-gray-500">— {e.notes}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FR({ label, value, mono }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className={`text-gray-900 ${mono ? "font-mono" : ""} text-sm`}>{value || "—"}</div>
    </div>
  );
}
